import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import request from '../../utils/request';

type CurrencyAmount = { currency: string; total_amount?: number; count?: number };
type BillCurrencyStat = {
  currency: string;
  count?: number;
  original_total?: number;
  adjust_delta?: number;
  total_payable?: number;
  total_paid?: number;
  total_unpaid?: number;
};
type OperationRange = {
  inbound_record_count: number;
  inbound_inventory_unit_total: number;
  inventory_decrease_total: number;
  instruction_created_count: number;
  instruction_completed_count: number;
  instruction_incomplete_count: number;
  billing_record_count: number | null;
  billing_total_amount_by_currency: CurrencyAmount[];
};
type WorkOrderSplit = { customer_batch: number; customer_single: number; internal: number };
type DashboardOverview = {
  scope: {
    is_admin: boolean;
    is_staff: boolean;
    is_customer: boolean;
    is_main_account: boolean;
    is_sub_account: boolean;
    can_view_finance: boolean;
  };
  stats: {
    customer_count: number | null;
    project_count: number;
    inventory_unit_type_count: number;
    inventory_unit_count: number;
    inventory_total_volume_m3: number;
    inventory_age_buckets: Record<string, number>;
    inventory_age_buckets_with_volume?: Record<string, { qty: number; volume_m3: number }>;
    funds_accounts: CurrencyAmount[];
    bills: BillCurrencyStat[];
  };
  operations: Record<string, OperationRange>;
  tasks: {
    pending_module_access_applications: number | null;
    pending_instructions: number | null;
    pending_instructions_breakdown?: {
      single_dropship?: number;
      regular_outbound?: number;
    };
    work_orders: {
      unclaimed: WorkOrderSplit;
      in_progress: WorkOrderSplit;
    };
  };
  quick_entries: Array<{ label: string; to: string }>;
};

const emptyOverview: DashboardOverview = {
  scope: {
    is_admin: false,
    is_staff: false,
    is_customer: false,
    is_main_account: false,
    is_sub_account: false,
    can_view_finance: false,
  },
  stats: {
    customer_count: null,
    project_count: 0,
    inventory_unit_type_count: 0,
    inventory_unit_count: 0,
    inventory_total_volume_m3: 0,
    inventory_age_buckets: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 },
    inventory_age_buckets_with_volume: {
      '0-30': { qty: 0, volume_m3: 0 },
      '31-60': { qty: 0, volume_m3: 0 },
      '61-90': { qty: 0, volume_m3: 0 },
      '90+': { qty: 0, volume_m3: 0 },
    },
    funds_accounts: [],
    bills: [],
  },
  operations: {},
  tasks: {
    pending_module_access_applications: null,
    pending_instructions: null,
    pending_instructions_breakdown: {
      single_dropship: 0,
      regular_outbound: 0,
    },
    work_orders: {
      unclaimed: { customer_batch: 0, customer_single: 0, internal: 0 },
      in_progress: { customer_batch: 0, customer_single: 0, internal: 0 },
    },
  },
  quick_entries: [],
};

const rangeLabelMap: Record<string, string> = {
  yesterday: '昨天',
  past_7_days: '过去1周',
  past_30_days: '过去1月',
  past_90_days: '过去3月',
};

const num = (v: unknown) => Number(v || 0);
  const signedNum = (v: unknown) => {
    const n = Number(v || 0);
    return `${n >= 0 ? '+' : '-'}${Math.abs(n)}`;
  };

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [overview, setOverview] = React.useState<DashboardOverview>(emptyOverview);
  const [activeRange, setActiveRange] = React.useState<keyof typeof rangeLabelMap>('yesterday');

  const loadOverview = React.useCallback(async () => {
    setLoading(true);
    try {
      const resp = await request.get('/api/v1/users/dashboard/overview/');
      setOverview(resp?.data || emptyOverview);
    } catch {
      setOverview(emptyOverview);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const scope = overview.scope || emptyOverview.scope;
  const showStats = scope.is_admin || scope.is_customer;
  const showOps = scope.is_admin || scope.is_customer;
  const showTasks = scope.is_admin || scope.is_staff;
  const showQuick = true;
  const activeOperation = overview.operations?.[activeRange];

  const pageTitle = scope.is_admin
    ? '管理员工作台'
    : scope.is_staff
      ? '员工工作台'
      : scope.is_sub_account
        ? '客户子账号工作台'
        : '客户主账号工作台';

  const roleBasePath = scope.is_admin ? '/dashboard/admin' : scope.is_staff ? '/dashboard/staff' : '/dashboard/customer';
  const buildTaskUrl = React.useCallback((path: string) => {
    const [pathname, query = ''] = path.split('?');
    const params = new URLSearchParams(query);
    params.set('from_dashboard', '1');
    params.set('jump_ts', String(Date.now()));
    return `${pathname}?${params.toString()}`;
  }, []);

  const taskLinkClassName = 'font-semibold text-blue-600 hover:underline';
  const capsuleBaseClass = 'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold';
  const currencySymbolMap: Record<string, string> = {
    USD: '$',
    CNY: '¥',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    HKD: 'HK$',
    CAD: 'C$',
    AUD: 'A$',
  };
  const currencyDisplay = React.useCallback((currency: string) => {
    const code = String(currency || '').toUpperCase();
    const symbol = currencySymbolMap[code];
    return symbol ? `${code}(${symbol})` : code;
  }, []);
  const currencyCapsuleStyleMap = React.useMemo(() => {
    const currencySet = new Set<string>();
    (overview.stats.funds_accounts || []).forEach((x) => currencySet.add(String(x.currency || '').toUpperCase()));
    (overview.stats.bills || []).forEach((x) => currencySet.add(String(x.currency || '').toUpperCase()));
    Object.values(overview.operations || {}).forEach((op) => {
      (op?.billing_total_amount_by_currency || []).forEach((x) => currencySet.add(String(x.currency || '').toUpperCase()));
    });
    const codes = Array.from(currencySet);
    const styleMap: Record<string, React.CSSProperties> = {};
    codes.forEach((code, idx) => {
      const hue = (idx * 137) % 360; // 黄金角保证当前页面币种颜色不重复
      styleMap[code] = {
        borderColor: `hsl(${hue} 75% 78%)`,
        backgroundColor: `hsl(${hue} 90% 96%)`,
        color: `hsl(${hue} 65% 30%)`,
      };
    });
    return styleMap;
  }, [overview.stats.funds_accounts, overview.stats.bills, overview.operations]);
  const ageCapsuleClassMap: Record<string, string> = {
    '0-30': `${capsuleBaseClass} border-green-200 bg-green-100 text-green-700`,
    '31-60': `${capsuleBaseClass} border-blue-200 bg-blue-100 text-blue-700`,
    '61-90': `${capsuleBaseClass} border-amber-200 bg-amber-100 text-amber-700`,
    '90+': `${capsuleBaseClass} border-red-200 bg-red-100 text-red-700`,
  };
  const taskCapsuleLinkClass = `${capsuleBaseClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`;
  const taskBatchCapsuleClass = `${capsuleBaseClass} border-green-200 bg-green-50 text-green-700 hover:bg-green-100`;
  const taskSingleCapsuleClass = `${capsuleBaseClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`;
  const taskInternalCapsuleClass = `${capsuleBaseClass} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`;
  const taskPendingPermissionCapsuleClass = `${capsuleBaseClass} border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100`;
  const taskPendingInstructionCapsuleClass = `${capsuleBaseClass} border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100`;
  const taskSingleDropshipCapsuleClass = `${capsuleBaseClass} border-red-500 bg-red-500 text-white hover:bg-red-600`;
  const taskRegularOutboundCapsuleClass = `${capsuleBaseClass} border-yellow-400 bg-yellow-400 text-white hover:bg-yellow-500`;
  const statLinkClassName = 'inline-block text-blue-600 hover:text-blue-700 hover:underline';
  const TaskLink: React.FC<{ to: string; children: React.ReactNode; className?: string }> = ({ to, children, className }) => (
    <Link
      to={buildTaskUrl(to)}
      onClick={(e) => {
        e.preventDefault();
        navigate(buildTaskUrl(to));
      }}
      className={className || taskLinkClassName}
    >
      {children}
    </Link>
  );
  const quickEntries = React.useMemo(() => {
    const base = scope.is_admin ? '/dashboard/admin' : scope.is_staff ? '/dashboard/staff' : '/dashboard/customer';
    const entries: Array<{ label: string; to: string }> = [];

    // 1) 创建项目（管理员/客户主账号）
    if (scope.is_admin || (scope.is_customer && scope.is_main_account)) {
      entries.push({ label: '创建项目', to: `${base}/projects/create` });
    }
    // 2) 扫描入库（管理员/员工）
    if (scope.is_admin || scope.is_staff) {
      entries.push({ label: '扫描入库', to: `${base}/inbound/scan` });
    }
    // 3) 入库记录（管理员/员工/客户主账号/客户子账号）
    if (scope.is_admin || scope.is_staff || scope.is_customer) {
      entries.push({ label: '入库记录', to: `${base}/inbound/records` });
    }
    // 4) 容器上架（管理员/员工）
    if (scope.is_admin || scope.is_staff) {
      entries.push({ label: '容器上架', to: `${base}/inbound/put-away` });
    }
    // 5) 当前库存（管理员/员工/客户主账号/客户子账号）
    if (scope.is_admin || scope.is_staff || scope.is_customer) {
      entries.push({ label: '当前库存', to: `${base}/inventory/current` });
    }
    // 6) 仓位表（管理员/员工）
    if (scope.is_admin || scope.is_staff) {
      entries.push({ label: '仓位表', to: `${base}/inventory/location` });
    }
    // 7) 创建指令（管理员/客户主账号/客户子账号）
    if (scope.is_admin || scope.is_customer) {
      entries.push({ label: '创建指令', to: `${base}/instructions/create` });
      // 8) 指令列表（管理员/客户主账号/客户子账号）
      entries.push({ label: '指令列表', to: `${base}/instructions/list` });
    }
    // 9) 资金账户（管理员/客户主账号）
    if (scope.is_admin || (scope.is_customer && scope.is_main_account)) {
      entries.push({ label: '资金账户', to: `${base}/finance/funds` });
    }
    // 10) 账单（管理员/客户主账号）
    if (scope.is_admin || (scope.is_customer && scope.is_main_account)) {
      entries.push({ label: '账单', to: `${base}/finance/bills` });
    }
    return entries;
  }, [scope]);

  const lowerSections: React.ReactNode[] = [];

  if (showOps) {
    lowerSections.push(
      <section key="ops" className="rounded-lg border bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-base font-semibold">业务记录</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(rangeLabelMap).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveRange(key as keyof typeof rangeLabelMap)}
                className={`rounded border px-2 py-1 text-xs ${
                  activeRange === key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeOperation ? (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">入库记录</div>
              <div className="mt-1 text-lg font-semibold">{num(activeOperation.inbound_record_count)}</div>
            </div>
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">入库数量</div>
              <div className="mt-1 text-lg font-semibold">{num(activeOperation.inbound_inventory_unit_total)}</div>
            </div>
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">库存扣减数量</div>
              <div className="mt-1 text-lg font-semibold">{num(activeOperation.inventory_decrease_total)}</div>
            </div>
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">创建指令数量</div>
              <div className="mt-1 text-lg font-semibold">
                {num(activeOperation.instruction_created_count)}{' '}
                <span className="text-xs font-normal text-gray-500">
                  (已完成 {num(activeOperation.instruction_completed_count)} | 未完成 {num(activeOperation.instruction_incomplete_count)})
                </span>
              </div>
            </div>
            {scope.can_view_finance ? (
              <>
                <div className="col-span-2 rounded border bg-gray-50 p-3 xl:col-span-2">
                  <div className="text-xs text-gray-500">收费记录</div>
                  <div className="mt-2 flex flex-col gap-2">
                    {activeOperation.billing_total_amount_by_currency?.length ? (
                      activeOperation.billing_total_amount_by_currency.map((x) => (
                        <div
                          key={`op-billing-${x.currency}`}
                          className={`${capsuleBaseClass} w-full flex-col items-center gap-0.5`}
                          style={currencyCapsuleStyleMap[String(x.currency || '').toUpperCase()]}
                        >
                          <span>{currencyDisplay(x.currency)}</span>
                          <span className="text-[11px] font-normal">
                            数量 {num(x.count)} | 金额: {num(x.total_amount)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500">暂无</div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div className="text-sm text-gray-500">暂无数据</div>
        )}
      </section>
    );
  }

  if (showTasks) {
    lowerSections.push(
      <section key="tasks" className="rounded-lg border bg-white p-4">
        <div className="mb-3 text-base font-semibold">当前任务</div>
        <div className="grid grid-cols-1 gap-3 text-sm">
          {scope.is_admin ? (
            <div className="rounded border bg-gray-50 p-3">
              <div className="flex flex-wrap gap-2">
                <TaskLink
                  to={`${roleBasePath}/customers/list?pending_review=1`}
                  className={taskPendingPermissionCapsuleClass}
                >
                  待审核权限申请 {num(overview.tasks.pending_module_access_applications)}
                </TaskLink>
                <TaskLink
                  to={`${roleBasePath}/instructions/list?filter_status=pending`}
                  className={taskPendingInstructionCapsuleClass}
                >
                  待审核指令 {num(overview.tasks.pending_instructions)}
                </TaskLink>
                <TaskLink
                  to={`${roleBasePath}/instructions/list?filter_status=pending&quick_template_system_code=InstructionTemplate-SYS-19`}
                  className={taskSingleDropshipCapsuleClass}
                >
                  一件代发 {num(overview.tasks.pending_instructions_breakdown?.single_dropship)}
                </TaskLink>
                <TaskLink
                  to={`${roleBasePath}/instructions/list?filter_status=pending&quick_template_system_code=InstructionTemplate-SYS-20`}
                  className={taskRegularOutboundCapsuleClass}
                >
                  常规出库 {num(overview.tasks.pending_instructions_breakdown?.regular_outbound)}
                </TaskLink>
              </div>
            </div>
          ) : null}
          <div className="rounded border bg-gray-50 p-3">
            <div className="font-medium">指令 - 待领取</div>
            <div className="mt-1 flex flex-wrap gap-2">
              <TaskLink
                to={`${roleBasePath}/execution?mode=batch&tab=todo`}
                className={taskBatchCapsuleClass}
              >
                客户指令-批次执行 {num(overview.tasks.work_orders.unclaimed.customer_batch)}
              </TaskLink>
              <TaskLink
                to={`${roleBasePath}/execution?mode=instruction&tab=todo`}
                className={taskSingleCapsuleClass}
              >
                客户指令-单指令执行 {num(overview.tasks.work_orders.unclaimed.customer_single)}
              </TaskLink>
              <TaskLink
                to={`${roleBasePath}/execution?mode=internal&tab=todo`}
                className={taskInternalCapsuleClass}
              >
                仓内工单 {num(overview.tasks.work_orders.unclaimed.internal)}
              </TaskLink>
            </div>
          </div>
          <div className="rounded border bg-gray-50 p-3">
            <div className="font-medium">指令 - 执行中</div>
            <div className="mt-1 flex flex-wrap gap-2">
              <TaskLink
                to={`${roleBasePath}/execution?mode=batch&tab=mine`}
                className={taskBatchCapsuleClass}
              >
                客户指令-批次执行 {num(overview.tasks.work_orders.in_progress.customer_batch)}
              </TaskLink>
              <TaskLink
                to={`${roleBasePath}/execution?mode=instruction&tab=mine`}
                className={taskSingleCapsuleClass}
              >
                客户指令-单指令执行 {num(overview.tasks.work_orders.in_progress.customer_single)}
              </TaskLink>
              <TaskLink
                to={`${roleBasePath}/execution?mode=internal&tab=mine`}
                className={taskInternalCapsuleClass}
              >
                仓内工单 {num(overview.tasks.work_orders.in_progress.internal)}
              </TaskLink>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (showQuick) {
    lowerSections.push(
      <section key="quick" className="rounded-lg border bg-white p-4">
        <div className="mb-3 text-base font-semibold">快捷入口</div>
        {quickEntries.length ? (
          <div className="grid grid-cols-2 gap-2">
            {quickEntries.map((x, idx) => (
              <Link
                key={`${x.to}-${idx}`}
                to={x.to}
                className="rounded border bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100"
              >
                {x.label}
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">暂无可用快捷入口</div>
        )}
      </section>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{pageTitle}</h1>
        </div>
        <button
          type="button"
          className="rounded border px-3 py-1.5 text-sm hover:bg-gray-50"
          onClick={loadOverview}
          disabled={loading}
        >
          {loading ? '刷新中...' : '刷新'}
        </button>
      </div>

      {showStats ? (
        <section className="rounded-lg border bg-white p-4">
          <div className="mb-3 text-base font-semibold">统计信息</div>
          <div className="grid grid-cols-2 gap-3 text-sm xl:grid-cols-4">
            {scope.is_admin ? (
              <div className="rounded border bg-gray-50 p-3">
                <div className="text-xs text-gray-500">
                  <Link
                    to={`${roleBasePath}/customers/list?sub_role=main_account`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`${roleBasePath}/customers/list?sub_role=main_account`);
                    }}
                    className={statLinkClassName}
                  >
                    有效客户数量
                  </Link>
                </div>
                <div className="mt-1 text-lg font-semibold">
                  <Link
                    to={`${roleBasePath}/customers/list?sub_role=main_account`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`${roleBasePath}/customers/list?sub_role=main_account`);
                    }}
                    className={statLinkClassName}
                  >
                    {num(overview.stats.customer_count)}
                  </Link>
                </div>
              </div>
            ) : null}
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">
                <Link
                  to={`${roleBasePath}/projects/list`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`${roleBasePath}/projects/list`);
                  }}
                  className={statLinkClassName}
                >
                  项目数量
                </Link>
              </div>
              <div className="mt-1 text-lg font-semibold">
                <Link
                  to={`${roleBasePath}/projects/list`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`${roleBasePath}/projects/list`);
                  }}
                  className={statLinkClassName}
                >
                  {num(overview.stats.project_count)}
                </Link>
              </div>
            </div>
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">库存单元种类</div>
              <div className="mt-1 text-lg font-semibold">{num(overview.stats.inventory_unit_type_count)}</div>
            </div>
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">
                <Link
                  to={`${roleBasePath}/inventory/current`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`${roleBasePath}/inventory/current`);
                  }}
                  className={statLinkClassName}
                >
                  库存数量
                </Link>
              </div>
              <div className="mt-1 text-lg font-semibold">
                <Link
                  to={`${roleBasePath}/inventory/current`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`${roleBasePath}/inventory/current`);
                  }}
                  className={statLinkClassName}
                >
                  {num(overview.stats.inventory_unit_count)}
                </Link>
              </div>
            </div>
            <div className="rounded border bg-gray-50 p-3">
              <div className="text-xs text-gray-500">总库容</div>
              <div className="mt-1 text-lg font-semibold">{num(overview.stats.inventory_total_volume_m3).toFixed(3)} m³</div>
            </div>
            <div className="col-span-2 rounded border bg-gray-50 p-3 xl:col-span-2">
              <div className="text-xs text-gray-500">库龄分布</div>
              <div className="mt-1 flex flex-wrap gap-2 text-sm">
                <span className={ageCapsuleClassMap['0-30']}>
                  0-30天 {num(overview.stats.inventory_age_buckets_with_volume?.['0-30']?.qty ?? overview.stats.inventory_age_buckets?.['0-30'])}件 {num(overview.stats.inventory_age_buckets_with_volume?.['0-30']?.volume_m3).toFixed(3)}m³
                </span>
                <span className={ageCapsuleClassMap['31-60']}>
                  31-60天 {num(overview.stats.inventory_age_buckets_with_volume?.['31-60']?.qty ?? overview.stats.inventory_age_buckets?.['31-60'])}件 {num(overview.stats.inventory_age_buckets_with_volume?.['31-60']?.volume_m3).toFixed(3)}m³
                </span>
                <span className={ageCapsuleClassMap['61-90']}>
                  61-90天 {num(overview.stats.inventory_age_buckets_with_volume?.['61-90']?.qty ?? overview.stats.inventory_age_buckets?.['61-90'])}件 {num(overview.stats.inventory_age_buckets_with_volume?.['61-90']?.volume_m3).toFixed(3)}m³
                </span>
                <span className={ageCapsuleClassMap['90+']}>
                  90天+ {num(overview.stats.inventory_age_buckets_with_volume?.['90+']?.qty ?? overview.stats.inventory_age_buckets?.['90+'])}件 {num(overview.stats.inventory_age_buckets_with_volume?.['90+']?.volume_m3).toFixed(3)}m³
                </span>
              </div>
            </div>
            {scope.can_view_finance ? (
              <>
                <div className="col-span-2 rounded border bg-gray-50 p-3 xl:col-span-1">
                  <div className="text-xs text-gray-500">资金账户可用余额</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {overview.stats.funds_accounts?.length
                      ? overview.stats.funds_accounts.map((x) => (
                        <span
                          key={`funds-${x.currency}`}
                          className={capsuleBaseClass}
                          style={currencyCapsuleStyleMap[String(x.currency || '').toUpperCase()]}
                        >
                          {currencyDisplay(x.currency)} {num(x.total_amount)}
                        </span>
                      ))
                      : <span className="text-sm text-gray-500">暂无</span>}
                  </div>
                </div>
                <div className="col-span-2 rounded border bg-gray-50 p-3 xl:col-span-2">
                  <div className="text-xs text-gray-500">账单（按币种）</div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs">
                    {overview.stats.bills?.length
                      ? overview.stats.bills.map((b) => (
                        <span
                          key={`bill-${b.currency}`}
                          className={`${capsuleBaseClass} flex-col items-start gap-0.5`}
                          style={currencyCapsuleStyleMap[String(b.currency || '').toUpperCase()]}
                        >
                          <span>{currencyDisplay(b.currency)} 共{num(b.count)}张账单</span>
                          <span className="whitespace-nowrap">
                            金额: 原始总额 {num(b.original_total)} | 调整 {signedNum(b.adjust_delta)} | 应付 {num(b.total_payable)} | 已付 {num(b.total_paid)} | 未支付 <span style={{ color: '#cf1322' }}>{num(b.total_unpaid)}</span>
                          </span>
                        </span>
                      ))
                      : <span className="text-sm text-gray-500">暂无</span>}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${Math.max(1, lowerSections.length)}, minmax(0, 1fr))`,
        }}
      >
        {lowerSections}
      </div>
    </div>
  );
};

export default Dashboard;